import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Users, Trophy } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageBanner } from "@/components/PageBanner";
import { fetchMeetBySlug, getDefaultThumbnail } from "@/lib/strapi";

export default function MeetArticlePage() {
  const { slug } = useParams();
  const [meet, setMeet] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const fallbackThumbnail = getDefaultThumbnail();

  const handleImageError = (event) => {
    if (event.currentTarget.src !== fallbackThumbnail) {
      event.currentTarget.src = fallbackThumbnail;
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let mounted = true;

    const loadMeet = async () => {
      try {
        setIsLoading(true);
        setError("");
        const data = await fetchMeetBySlug(slug);
        if (mounted) {
          setMeet(data);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Failed to load meet");
          setMeet(null);
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    loadMeet();

    return () => {
      mounted = false;
    };
  }, [slug]);

  return (
    <div data-testid="meet-article-page" className="min-h-screen">
      <Header />

      {isLoading && (
        <section className="pt-[72px] bg-amber-pale min-h-[50vh]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-20 text-warm-gray font-inter text-sm">
            Loading meet details...
          </div>
        </section>
      )}

      {!isLoading && !!error && (
        <section className="pt-[72px] bg-amber-pale min-h-[50vh]">
          <div className="max-w-4xl mx-auto px-6 md:px-12 py-20">
            <p className="font-inter text-sm text-red-600 mb-6">Unable to load this meet right now.</p>
            <Link to="/meets" className="inline-flex items-center gap-2 font-inter text-sm text-amber-dusk">
              <ArrowLeft size={14} />
              Back to Meets
            </Link>
          </div>
        </section>
      )}

      {!isLoading && !error && meet && (
        <>
          <PageBanner
            eyebrow={meet.type || "Competition Meet"}
            title={meet.name}
            description={meet.description || "Meet profile and details."}
            image={meet.image}
          />

          <section className="bg-white py-16 md:py-20">
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <div className="flex flex-wrap items-center gap-4 mb-8">
                <Link
                  to="/meets"
                  className="inline-flex items-center gap-2 font-inter text-sm text-amber-dusk hover:text-solar-orange transition-colors"
                >
                  <ArrowLeft size={14} />
                  Back to Meets
                </Link>
                {!!meet.location && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <MapPin size={12} />
                    <span className="font-inter font-light text-xs">{meet.location}</span>
                  </div>
                )}
                {!!meet.date && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <Clock size={12} />
                    <span className="font-inter font-light text-xs">{meet.date}</span>
                  </div>
                )}
                {!!meet.participants && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <Users size={12} />
                    <span className="font-inter font-light text-xs">{meet.participants} athletes</span>
                  </div>
                )}
                {!!meet.type && (
                  <div className="inline-flex items-center gap-2 text-warm-gray">
                    <Trophy size={12} />
                    <span className="font-inter font-light text-xs">{meet.type}</span>
                  </div>
                )}
                {!!meet.registrationUrl && (
                  <a
                    href={meet.registrationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 font-inter text-sm text-white bg-solar-orange hover:bg-solar-orange/90 transition-colors px-4 py-2 rounded-sm ml-auto"
                  >
                    Register Now
                  </a>
                )}
              </div>

              <div className="relative overflow-hidden rounded-sm aspect-[16/9] mb-8 border border-near-black/10">
                <img
                  src={meet.image}
                  alt={meet.name}
                  loading="lazy"
                  onError={handleImageError}
                  className="w-full h-full object-cover"
                />
              </div>

              {!!meet.body ? (
                <article
                  className="article-content"
                  dangerouslySetInnerHTML={{ __html: meet.body }}
                />
              ) : (
                <p className="font-inter text-base text-warm-gray leading-relaxed">{meet.description || "No additional details available yet."}</p>
              )}
            </div>
          </section>
        </>
      )}

      <Footer />
    </div>
  );
}
